import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  GitFork,
  Bot,
} from 'lucide-react';
import type { TypedToolPart, ToolState } from '../../types/chat';
import { agentStyles } from '../DelegationBadge';

interface DelegationGroupProps {
  parts: TypedToolPart[];
}

const defaultStyle = {
  color: 'text-gray-700 dark:text-gray-200',
  bgColor: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
};

function slugFromToolName(part: TypedToolPart): string {
  if (part.toolName === 'delegate_to_remote') {
    return (part.input?.agent_name as string) || 'remote';
  }
  return part.toolName.replace('delegate_to_', '');
}

function displayName(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

function stateIcon(state: ToolState) {
  switch (state) {
    case 'input-streaming':
    case 'input-available':
      return <Loader className="w-3.5 h-3.5 animate-spin text-sky-500" />;
    case 'output-available':
      return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    case 'output-error':
      return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  }
}

function stateLabel(state: ToolState): string {
  switch (state) {
    case 'input-streaming':
      return 'Preparing...';
    case 'input-available':
      return 'Running...';
    case 'output-available':
      return 'Completed';
    case 'output-error':
      return 'Failed';
  }
}

function isRunning(state: ToolState): boolean {
  return state === 'input-streaming' || state === 'input-available';
}

/**
 * Groups 2+ delegation TypedToolParts into a single collapsed/expandable card.
 */
export function DelegationGroup({ parts }: DelegationGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const running = parts.filter((p) => isRunning(p.state)).length;
  const completed = parts.filter((p) => p.state === 'output-available').length;
  const failed = parts.filter((p) => p.state === 'output-error').length;

  const allDone = running === 0;
  const allCompleted = completed === parts.length;

  // Summary text
  const summaryParts: string[] = [];
  if (completed > 0) summaryParts.push(`${completed} completed`);
  if (running > 0) summaryParts.push(`${running} running`);
  if (failed > 0) summaryParts.push(`${failed} failed`);
  const summaryText = summaryParts.join(' \u00b7 ');

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div
      className={`my-1 rounded-lg border overflow-hidden transition-all duration-200 ${
        allCompleted
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          : failed > 0
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800'
      }`}
      data-testid="delegation-group"
    >
      {/* Header */}
      <button
        type="button"
        className="flex items-center gap-2 px-3 py-2 w-full text-left bg-transparent border-0 cursor-pointer hover:bg-black/5"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <GitFork className="w-4 h-4 text-sky-600 dark:text-sky-400 flex-shrink-0" />
        <span className="font-medium text-sm text-gray-700 dark:text-gray-200">
          Delegating to {parts.length} agents
        </span>

        {/* Status summary */}
        <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ml-auto mr-1">
          {!allDone && <Loader className="w-3 h-3 animate-spin text-sky-500" />}
          {allCompleted && <CheckCircle className="w-3 h-3 text-green-500" />}
          {failed > 0 && !allCompleted && <XCircle className="w-3 h-3 text-red-500" />}
          {summaryText}
        </span>

        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        )}
      </button>

      {/* Expanded: per-agent rows */}
      {expanded && (
        <div className="border-t border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-gray-900/50">
          {parts.map((part, idx) => {
            const slug = slugFromToolName(part);
            const name = displayName(slug);
            const style = agentStyles[slug] || defaultStyle;
            const rowExpanded = expandedRows.has(idx);
            const hasDetails =
              (part.input && Object.keys(part.input).length > 0) ||
              part.output ||
              part.errorText;

            return (
              <div key={part.toolCallId || idx}>
                <button
                  type="button"
                  className={`flex items-center gap-2 px-3 py-1.5 w-full text-left bg-transparent border-0 text-sm ${
                    hasDetails ? 'cursor-pointer hover:bg-black/5' : ''
                  }`}
                  onClick={hasDetails ? () => toggleRow(idx) : undefined}
                  disabled={!hasDetails}
                >
                  {stateIcon(part.state)}
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded border ${style.bgColor} ${style.color}`}
                  >
                    <Bot className="w-3 h-3" />
                    {name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {stateLabel(part.state)}
                  </span>
                  {hasDetails && (
                    <span className="ml-auto text-gray-400 dark:text-gray-500">
                      {rowExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                    </span>
                  )}
                </button>

                {/* Sub-row detail */}
                {rowExpanded && hasDetails && (
                  <div className="px-4 pb-2 pt-1 ml-6 space-y-2 border-l-2 border-gray-200 dark:border-gray-700">
                    {part.input && Object.keys(part.input).length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Input
                        </div>
                        <pre className="text-xs bg-white dark:bg-gray-900 rounded-md p-2 overflow-x-auto border border-gray-100 dark:border-gray-800 font-mono">
                          {JSON.stringify(part.input, null, 2)}
                        </pre>
                      </div>
                    )}
                    {part.state === 'output-available' && part.output !== undefined && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Output
                        </div>
                        <pre className="text-xs bg-white dark:bg-gray-900 rounded-md p-2 overflow-x-auto max-h-48 border border-gray-100 dark:border-gray-800 font-mono">
                          {typeof part.output === 'string'
                            ? part.output
                            : JSON.stringify(part.output, null, 2)}
                        </pre>
                      </div>
                    )}
                    {part.state === 'output-error' && part.errorText && (
                      <div>
                        <div className="text-xs font-medium text-red-500 dark:text-red-400 mb-1">
                          Error
                        </div>
                        <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md p-2 overflow-x-auto border border-red-100 dark:border-red-800 font-mono">
                          {part.errorText}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
